<div class="mb-3">
  <h1 class="fs-5 fw-bold mb-0">Funnel statistics</h1>
</div>

<div class="card border-0 shadow mb-3 fade-in-zoom">
  <div class="card-body">
    <div class="row mb-3">
      <div class="col-5">
        <app-date-range-selector #periodSelector [date]="initialPeriod.preset" [zone]="'Europe/Paris'"
          [initialFrom]="initialPeriod.from" [initialTo]="initialPeriod.to" (init)="apply()"></app-date-range-selector>
      </div>

      <div class="col-3">
        <label for="funnelDomain" class="form-label fw-bold small">Domain</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-globe"></i></span>
          <select id="funnelDomain" class="form-select" [(ngModel)]="domainId">
            <option [ngValue]="null">All domains</option>
            @for (domain of domains; track domain.id) {
              <option [ngValue]="domain.id">{{ domain.domain }}</option>
            }
          </select>
        </div>
      </div>

      <div class="col-4">
        <label for="funnelSearch" class="form-label fw-bold small">Funnel</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input id="funnelSearch" type="text" class="form-control" placeholder="Search in name or slug" [(ngModel)]="search"
            (keyup.enter)="apply()">
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-3">
        <label class="form-label fw-bold small">Brand</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-box"></i></span>
          <select class="form-select" [(ngModel)]="productId" (ngModelChange)="changeProduct()">
            <option [ngValue]="null">{{ getProductPlaceholder() }}</option>
            @for (product of products; track product.id) {
              <option [ngValue]="product.id">{{ product.name }}</option>
            }
          </select>
        </div>
      </div>

      <div class="col-3">
        <label class="form-label fw-bold small">Offer</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-basket"></i></span>
          <select class="form-select" [(ngModel)]="offerId" [appElementLoader]="offerLoading">
            <option [ngValue]="null">{{ getOfferPlaceholder() }}</option>
            @for (offer of offers; track offer.id) {
              <option [ngValue]="offer.id">{{ offer.name }}</option>
            }
          </select>
        </div>
      </div>

      <div class="col-6">
        <label class="form-label fw-bold small">Partner</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-diagram-3"></i></span>
          <app-dropdown-select [(ngModel)]="partnerIds" [multiple]="true" [selectAll]="true">
            @for (partner of partners; track partner.id) {
              <app-dropdown-select-option [value]="partner.id">{{ partner }}</app-dropdown-select-option>
            }
          </app-dropdown-select>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      @for (field of trackingFields; track field) {
        <div class="col">
          <label [for]="'funnel-' + field" class="form-label fw-bold small text-uppercase">{{ field }}</label>
          <input [id]="'funnel-' + field" type="text" class="form-control" [(ngModel)]="tracking[field]"
            (keyup.enter)="apply()">
        </div>
      }
    </div>

    <div class="row">
      <div class="col-2">
        <label for="funnelMinLeads" class="form-label fw-bold small"
          title="Hide funnels with fewer form submissions (OK + Reactivated + AR + KO) over the period">Min. submissions</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-funnel"></i></span>
          <input id="funnelMinLeads" type="number" min="0" class="form-control" [(ngModel)]="minLeads">
        </div>
      </div>

      <div class="col-10 d-flex align-items-end justify-content-end">
        <button type="button" class="btn btn-outline-primary me-2" (click)="saveFilters()" title="Save filters">
          <i class="bi bi-floppy"></i>
        </button>
        <button type="button" class="btn btn-outline-secondary me-2" (click)="clearFilters()" title="Clear filters">
          <i class="bi bi-eraser"></i>
        </button>
        <button type="submit" class="btn btn-primary fw-bold" [disabled]="loading" (click)="apply()">Apply filters</button>
      </div>
    </div>

    @if (error) {
      <div class="alert alert-danger small py-2 mt-3 mb-0">
        <i class="bi bi-exclamation-triangle me-1"></i>{{ error }}
      </div>
    }
  </div>
</div>

@if (loaded && totals) {
  <div class="row g-3 mb-3 stat-tiles">
    <div class="col">
      <div class="card border-0 shadow h-100 stat-tile">
        <div class="card-body">
          <div class="stat-label">Visitors<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.visitors"></i></div>
          <div class="stat-value">{{ count(totals.visitors) }}</div>
          <div class="stat-hint">in {{ count(totals.funnels) }} funnels</div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card border-0 shadow h-100 stat-tile stat-tile-hero">
        <div class="card-body">
          <div class="stat-label">Conversion<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.conversion"></i></div>
          <div class="stat-value stat-hero">{{ percent(totals.conversionRate) }}</div>
          <div class="stat-hint">{{ count(totals.converted) }} converted visitors</div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card border-0 shadow h-100 stat-tile">
        <div class="card-body">
          <div class="stat-label">Reactivation<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.reactivation"></i></div>
          <div class="stat-value text-reactivated">{{ percent(totals.reactivationRate) }}</div>
          <div class="stat-hint">{{ count(totals.reactivated) }} reactivated visitors</div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card border-0 shadow h-100 stat-tile">
        <div class="card-body">
          <div class="stat-label">Submissions<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.submissions"></i></div>
          <div class="stat-value">{{ count(totals.leadTotal) }}</div>
          <div class="stat-hint">
            <span class="text-ok">{{ count(totals.leadOk) }} OK</span> ·
            <span class="text-reactivated">{{ count(totals.leadReactivated) }} reactivated</span> ·
            <span class="text-ar">{{ count(totals.leadAr) }} AR</span> ·
            <span class="text-ko">{{ count(totals.leadKo) }} KO</span>
          </div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card border-0 shadow h-100 stat-tile">
        <div class="card-body">
          <div class="stat-label">Accept rate<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.acceptRate"></i></div>
          <div class="stat-value">{{ percent(totals.acceptRate) }}</div>
          <div class="stat-hint">of submissions are OK</div>
        </div>
      </div>
    </div>
    @if (totals.leadXsell) {
      <div class="col">
        <div class="card border-0 shadow h-100 stat-tile">
          <div class="card-body">
            <div class="stat-label">Xsell accepted<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.xsell"></i></div>
            <div class="stat-value">{{ count(totals.leadXsell) }}</div>
          </div>
        </div>
      </div>
    }
  </div>

  @if (totals.visitors) {
    <div class="card border-0 shadow mb-3">
      <div class="card-body">
        <div class="stat-label mb-2">What the {{ count(totals.visitors) }} visitors did<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.outcomes"></i></div>
        <ng-container *ngTemplateOutlet="outcomeBar; context: {$implicit: outcomeSegments(totals), total: totals.visitors}"></ng-container>
      </div>
    </div>
  }

  @if (!funnels.length) {
    <div class="card border-0 shadow mb-3">
      <div class="card-body text-center text-secondary py-5">
        <i class="bi bi-funnel fs-1 d-block mb-2"></i>
        No funnel activity recorded for this period.
      </div>
    </div>
  } @else {
    <div class="card border-0 shadow mb-3">
      <div class="card-header bg-tertiary p-3 border-0 d-flex align-items-center flex-wrap gap-2">
        <h2 class="fs-6 fw-bold mb-0 me-auto">
          <i class="bi bi-trophy me-2"></i>Ranking by conversion<i class="bi bi-info-circle ms-1 info-icon"
            [appTooltip]="'Funnels sorted by conversion (converted visitors / visitors). Funnels with fewer than ' + minRankedVisitors + ' visitors are listed at the end without a rank, because their rate is not reliable yet.'"></i>
        </h2>
        <span class="small text-secondary">
          <span class="tone-dot tone-good"></span>≥ {{ percent(totals.conversionRate) }} (average)
          <span class="tone-dot tone-warning ms-3"></span>{{ percent(halfAverage()) }} – {{ percent(totals.conversionRate) }}
          <span class="tone-dot tone-serious ms-3"></span>&lt; {{ percent(halfAverage()) }}
        </span>
      </div>
      <div class="card-body p-0">
        <table class="table table-hover align-middle mb-0 ranking">
          <thead>
            <tr>
              <th class="text-center">#</th>
              <th>Funnel</th>
              <th class="text-end">Visitors<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.visitors"></i></th>
              <th class="conversion-column">Conversion<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.conversion"></i></th>
              <th class="text-end">Reactivation<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.reactivation"></i></th>
              <th class="outcome-column">What visitors did<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.outcomes"></i></th>
              <th class="text-end">Submissions<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.submissions"></i></th>
              <th class="text-end">Accept rate<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.acceptRate"></i></th>
            </tr>
          </thead>
          <tbody>
            @for (view of rankingViews(); track view.funnel.id) {
              <tr role="button" (click)="scrollTo(view.funnel)" [class.unranked]="view.funnel.rank === null">
                <td class="text-center">
                  @if (view.funnel.rank !== null) {
                    <span class="rank-badge" [ngClass]="'rank-' + view.funnel.rank">{{ view.funnel.rank }}</span>
                  } @else {
                    <span class="text-secondary" [title]="'Not ranked: fewer than ' + minRankedVisitors + ' visitors'">–</span>
                  }
                </td>
                <td>
                  <div class="fw-bold">{{ view.funnel.name }}</div>
                  <div class="small text-secondary">{{ view.funnel.domain?.domain ?? 'No domain' }}</div>
                </td>
                <td class="text-end">{{ count(view.funnel.visitors) }}</td>
                <td class="conversion-column">
                  <div class="d-flex align-items-center gap-2">
                    <div class="meter flex-grow-1" [ngClass]="'meter-' + view.tone">
                      <div class="meter-fill" [style.width]="meterWidth(view.funnel.conversionRate)"></div>
                    </div>
                    <span class="meter-value">{{ percent(view.funnel.conversionRate) }}</span>
                  </div>
                  <div class="small text-secondary">
                    {{ count(view.funnel.converted) }} converted
                    @if (view.funnel.rank !== null) {
                      · {{ delta(view.funnel.conversionRate) }} vs average
                    }
                  </div>
                </td>
                <td class="text-end">
                  <div class="text-reactivated fw-bold">{{ percent(view.funnel.reactivationRate) }}</div>
                  <div class="small text-secondary">{{ count(view.funnel.reactivated) }} reactivated</div>
                </td>
                <td class="outcome-column">
                  <div class="segment-bar">
                    @for (segment of outcomeSegments(view.funnel); track segment.key) {
                      <div [ngClass]="'seg-' + segment.key" [style.width]="segment.width" [title]="segment.title"></div>
                    }
                  </div>
                </td>
                <td class="text-end">
                  <div>{{ count(view.funnel.leadTotal) }}</div>
                  <div class="small text-secondary">
                    <span class="text-ok">{{ count(view.funnel.leadOk) }} OK</span> ·
                    <span class="text-reactivated">{{ count(view.funnel.leadReactivated) }} reactivated</span> ·
                    <span class="text-ar">{{ count(view.funnel.leadAr) }} AR</span> ·
                    <span class="text-ko">{{ count(view.funnel.leadKo) }} KO</span>
                  </div>
                </td>
                <td class="text-end fw-bold">{{ percent(view.funnel.acceptRate) }}</td>
              </tr>
            }
          </tbody>
        </table>
        <div class="p-2 small text-secondary text-center border-top">
          @if (rankingHasMore()) {
            <a href="javascript:void(0)" class="fw-bold me-3" (click)="toggleRanking()">
              @if (rankingExpanded) {
                <i class="bi bi-chevron-up me-1"></i>Show less
              } @else {
                <i class="bi bi-chevron-down me-1"></i>Show all {{ views.length }} funnels
              }
            </a>
          }
          {{ rankedCount() }} funnels ranked · funnels with fewer than {{ minRankedVisitors }} visitors are listed at the end without a rank.
          Click a row to see its pages.
        </div>
      </div>
    </div>

    @for (view of cardViews(); track view.funnel.id) {
      <div class="card border-0 shadow mb-3 funnel-card" [id]="'funnel-stats-' + view.funnel.id"
        [ngClass]="{'funnel-card-highlight': highlightedFunnelId === view.funnel.id}">
        <div class="card-header bg-tertiary p-3 border-0 d-flex align-items-center flex-wrap gap-3">
          @if (view.funnel.rank !== null) {
            <span class="rank-badge rank-badge-lg" [ngClass]="'rank-' + view.funnel.rank">{{ view.funnel.rank }}</span>
          }
          <div class="me-auto">
            <h2 class="fs-6 fw-bold mb-0">{{ view.funnel.name }}</h2>
            <div class="small text-secondary">
              @if (funnelUrl(view.funnel); as url) {
                <a [href]="url" target="_blank" rel="noopener" class="text-secondary">{{ url }}</a>
              } @else {
                {{ view.funnel.domain?.domain ?? 'No domain' }}
              }
              <a [routerLink]="['/promotion-tools/cms/funnels', view.funnel.id]" class="ms-2 text-secondary"
                title="Open the funnel editor"><i class="bi bi-pencil"></i></a>
            </div>
          </div>
        </div>

        <div class="card-body p-0 d-flex funnel-body">
          <aside class="funnel-summary">
            <div class="funnel-kpi">
              <div class="stat-label">Visitors<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.visitors"></i></div>
              <div class="funnel-kpi-value">{{ count(view.funnel.visitors) }}</div>
            </div>
            <div class="funnel-kpi" [ngClass]="'tone-text-' + view.tone">
              <div class="stat-label">Conversion<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.conversion"></i></div>
              <div class="funnel-kpi-value funnel-kpi-tone">{{ percent(view.funnel.conversionRate) }}</div>
              <div class="funnel-kpi-hint">
                {{ count(view.funnel.converted) }} converted<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.converted"></i>
                @if (view.funnel.rank !== null) {
                  · {{ delta(view.funnel.conversionRate) }} vs average
                }
              </div>
              @if (view.funnel.leadOk) {
                <div class="funnel-kpi-hint">
                  {{ count(view.funnel.leadOk) }} OK sent
                  @if (view.funnel.multiOk) {
                    · {{ count(view.funnel.multiOk) }} visitors sent several
                  }
                  <i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.okSent"></i>
                </div>
              }
              @if (view.funnel.convertedStopped && hasNextPage(view.funnel)) {
                <div class="funnel-kpi-hint">
                  {{ count(view.funnel.convertedStopped) }} converted saw no next page<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.convertedStopped"></i>
                </div>
              }
            </div>
            <div class="funnel-kpi">
              <div class="stat-label">Reactivation<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.reactivation"></i></div>
              <div class="funnel-kpi-value text-reactivated">{{ percent(view.funnel.reactivationRate) }}</div>
              <div class="funnel-kpi-hint">{{ count(view.funnel.reactivated) }} reactivated<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.reactivated"></i></div>
            </div>
            @if (view.funnel.visitors) {
              <ng-container *ngTemplateOutlet="outcomeList; context: {$implicit: outcomeSegments(view.funnel), total: view.funnel.visitors}"></ng-container>
            }
            @if (view.funnel.leadTotal) {
              <div class="funnel-kpi">
                <div class="stat-label">Submissions<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.submissions"></i></div>
                <div class="funnel-kpi-value">{{ count(view.funnel.leadTotal) }}</div>
                <div class="funnel-kpi-hint">
                  <span class="text-ok">{{ count(view.funnel.leadOk) }} OK</span> ·
                  <span class="text-reactivated">{{ count(view.funnel.leadReactivated) }} reactivated</span> ·
                  <span class="text-ar">{{ count(view.funnel.leadAr) }} AR</span> ·
                  <span class="text-ko">{{ count(view.funnel.leadKo) }} KO</span>
                </div>
                <div class="funnel-kpi-hint">{{ percent(view.funnel.acceptRate) }} accept rate<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.acceptRate"></i></div>
              </div>
            }
            @if (view.funnel.leadXsell) {
              <div class="funnel-kpi">
                <div class="stat-label">Xsell accepted<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.xsell"></i></div>
                <div class="funnel-kpi-value">{{ count(view.funnel.leadXsell) }}</div>
              </div>
            }
          </aside>

          <div class="flow-body">
            <div #flow class="flow" [attr.data-funnel-id]="view.funnel.id">
              @if (layouts[view.funnel.id]; as layout) {
                <svg class="flow-arrows" [attr.width]="layout.width" [attr.height]="layout.height">
                  <defs>
                    <marker [attr.id]="'arrow-head-' + view.funnel.id" viewBox="0 0 10 10" refX="9" refY="5"
                      markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto-start-reverse">
                      <path d="M0,0 L10,5 L0,10 z"></path>
                    </marker>
                  </defs>
                  @for (arrow of layout.arrows; track arrow.key) {
                    <path [attr.d]="arrow.d" [attr.stroke-width]="arrow.width" [ngClass]="{backward: arrow.backward}"
                      [attr.marker-end]="'url(#arrow-head-' + view.funnel.id + ')'" fill="none">
                      <title>{{ arrow.title }}</title>
                    </path>
                  }
                </svg>
                @for (arrow of layout.arrows; track arrow.key) {
                  <span class="flow-arrow-label" [style.left.px]="arrow.labelX" [style.top.px]="arrow.labelY"
                    [title]="arrow.title">{{ arrow.label }}</span>
                }
              }

              @for (level of view.levels; track $index) {
                <div class="flow-level">
                  @for (page of level; track page.id) {
                    <div class="flow-card" [attr.data-page-id]="page.id"
                      [ngClass]="{'flow-card-start': page.start, 'flow-card-orphan': !page.reachable, 'flow-card-url': page.type === 'url'}">
                      <div class="flow-card-head">
                        <span class="flow-card-step">
                          @if (page.start) {
                            <i class="bi bi-play-fill"></i>
                          } @else if (page.type === 'url') {
                            <i class="bi bi-box-arrow-up-right"></i>
                          } @else {
                            <i class="bi bi-file-earmark"></i>
                          }
                          {{ pageTypeLabel(page) }}
                        </span>
                        @if (!page.reachable) {
                          <span class="flow-card-flag text-secondary" title="Not linked from the entry page"><i class="bi bi-link-45deg"></i></span>
                        }
                        @if (page.terminal) {
                          <span class="flow-card-flag flow-card-end" title="Last page of the funnel"><i class="bi bi-flag-fill"></i></span>
                        }
                      </div>
                      <div class="flow-card-name" [title]="page.url ?? page.slug ?? ''">{{ page.name }}</div>

                      <div class="flow-card-visitors">
                        <span class="flow-card-visitors-value">{{ count(page.visitors) }}</span>
                        <span class="flow-card-visitors-label">visitors<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.pageVisitors"></i></span>
                      </div>
                      @if (!page.start && page.visitors) {
                        <div class="flow-card-origin">{{ originSummary(page) }}<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.pageOrigin"></i></div>
                      }

                      @if (page.leadTotal) {
                        <div class="flow-card-leads">
                          <div class="flow-card-leads-row">
                            <span><strong>{{ count(page.leadTotal) }}</strong> submissions</span>
                            <span class="flow-card-accept">{{ percent(page.acceptRate) }} OK<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.pageSubmissions"></i></span>
                          </div>
                          <div class="segment-bar">
                            @for (segment of leadSegments(page); track segment.key) {
                              <div [ngClass]="'seg-' + segment.key" [style.width]="segment.width" [title]="segment.title"></div>
                            }
                          </div>
                          <div class="flow-card-leads-detail">
                            <span class="text-ok">{{ count(page.leadOk) }} OK</span> ·
                            <span class="text-reactivated">{{ count(page.leadReactivated) }} reactivated</span> ·
                            <span class="text-ar">{{ count(page.leadAr) }} AR</span> ·
                            <span class="text-ko">{{ count(page.leadKo) }} KO</span>
                          </div>
                        </div>
                      }
                      @if (page.leadP0 || page.leadXsell) {
                        <div class="flow-card-extra">
                          @if (page.leadP0) {
                            <div title="Pre-registrations (p0 leads) sent from this page"><strong>{{ count(page.leadP0) }}</strong> prospects (p0)</div>
                          }
                          @if (page.leadXsell) {
                            <div><strong>{{ count(page.leadXsell) }}</strong> xsell accepted<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.xsell"></i></div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    }
  }
}

<ng-template #outcomeBar let-segments let-total="total">
  <div class="segment-bar segment-bar-lg">
    @for (segment of segments; track segment.key) {
      <div [ngClass]="'seg-' + segment.key" [style.width]="segment.width" [title]="segment.title"></div>
    }
  </div>
  <div class="segment-legend">
    @for (segment of segments; track segment.key) {
      <span [title]="segment.title">
        <span class="legend-dot" [ngClass]="'seg-' + segment.key"></span>{{ segment.label }}
        <strong>{{ count(segment.count) }}</strong>
        <span class="text-secondary">({{ percent(segment.count / total) }})</span>
      </span>
    }
  </div>
</ng-template>

<ng-template #outcomeList let-segments let-total="total">
  <div class="funnel-kpi">
    <div class="stat-label mb-1">What visitors did<i class="bi bi-info-circle ms-1 info-icon" [appTooltip]="help.outcomes"></i></div>
    <div class="segment-bar mb-1">
      @for (segment of segments; track segment.key) {
        <div [ngClass]="'seg-' + segment.key" [style.width]="segment.width" [title]="segment.title"></div>
      }
    </div>
    @for (segment of segments; track segment.key) {
      <div class="outcome-row" [title]="segment.title">
        <span><span class="legend-dot" [ngClass]="'seg-' + segment.key"></span>{{ segment.label }}</span>
        <span>{{ count(segment.count) }} <span class="text-secondary">· {{ percent(segment.count / total) }}</span></span>
      </div>
    }
  </div>
</ng-template>
